import type { CogniteClient, QueryRequest } from '@cognite/sdk';
import { produce } from 'immer';

/** Alternative query suggestion generated from a failed query. */
interface AlternativeQuery {
  description: string;
  query: unknown;
}

/** Context for query recovery operations. */
interface RecoveryContext {
  client: CogniteClient;
  maxRetries?: number;
  onRetry?: (attempt: number, relaxedQuery: unknown) => void;
}

/** Result of query execution with recovery. */
interface RecoveryResult<T> {
  success: boolean;
  items?: T;
  attempts: number;
  finalQuery: unknown;
  error?: string;
  suggestedAlternatives?: AlternativeQuery[];
}

/** Default maximum retry count for query recovery */
const DEFAULT_MAX_RETRIES = 3;

/**
 * CDF instance-query hard per-page cap. The API rejects any single page with a
 * higher `limit` than this. Pagination in {@link executeQueryWithRecovery}
 * splits the caller's total budget into pages no larger than this value.
 */
export const CDF_MAX_PAGE_SIZE = 1000;

/**
 * Upper bound on the total number of instances a single knowledge-graph query
 * can retrieve across paginated CDF calls. Surfaced through the KG query
 * parameter schema's `limit.max` and converted into up to
 * `KG_QUERY_MAX_LIMIT / CDF_MAX_PAGE_SIZE` round-trips per query.
 */
export const KG_QUERY_MAX_LIMIT = 10_000;
/**
 * Read the TOTAL row budget out of a CDF instance query. The per-view `limit`
 * on the incoming query is treated as the caller's desired total; it is
 * split across pages by {@link executeQueryWithRecovery}. Defaults to one
 * page when no numeric limit is found.
 */
function getTotalBudget(query: QueryRequest): number {
  const withSection = (query as { with?: Record<string, unknown> }).with;
  if (!withSection) {
    return CDF_MAX_PAGE_SIZE;
  }
  for (const vc of Object.values(withSection)) {
    const limit = (vc as { limit?: unknown } | undefined)?.limit;
    if (typeof limit === 'number') {
      return limit;
    }
  }
  return CDF_MAX_PAGE_SIZE;
}

/**
 * Return a copy of `query` with every view config's `limit` replaced by
 * `perPageLimit`. Used to cap each paginated round-trip at
 * {@link CDF_MAX_PAGE_SIZE} without mutating the caller's query.
 */
function withPerViewLimit(query: QueryRequest, perPageLimit: number): QueryRequest {
  return produce(query, (draft) => {
    const withSection = (draft as { with?: Record<string, unknown> }).with;
    if (!withSection) {
      return;
    }
    for (const viewKey of Object.keys(withSection)) {
      const vc = withSection[viewKey] as Record<string, unknown> | undefined;
      if (vc && typeof vc === 'object') {
        vc.limit = perPageLimit;
      }
    }
  });
}

/** Sum the row counts across all view-keyed item arrays in a CDF query result. */
function countItems(items: Record<string, unknown[]> | undefined): number {
  if (!items) {
    return 0;
  }
  return Object.values(items).reduce((sum, arr) => sum + (arr?.length ?? 0), 0);
}
/**
 * Append paginated page items into the accumulator, keyed by view. New view
 * keys that were not present on the first page are ignored (paginated pages
 * never introduce new view keys for our single-view queries).
 */
function appendPageItems(
  accumulator: Record<string, unknown[]>,
  pageItems: Record<string, unknown[]> | undefined,
): void {
  if (!pageItems) {
    return;
  }
  for (const key of Object.keys(accumulator)) {
    const pageArr = pageItems[key];
    if (pageArr) {
      accumulator[key]?.push(...pageArr);
    }
  }
}
/**
 * Drive paginated CDF instance-query fetches against a single `baseQuery`,
 * splitting `totalBudget` into pages of at most {@link CDF_MAX_PAGE_SIZE}.
 * Returns the merged view-keyed item map. Stops when the cursor is exhausted,
 * a page returns no new items, or the budget is reached.
 */
async function paginatePages(
  client: CogniteClient,
  baseQuery: QueryRequest,
  totalBudget: number,
): Promise<Record<string, unknown[]>> {
  const firstQuery = withPerViewLimit(baseQuery, Math.min(totalBudget, CDF_MAX_PAGE_SIZE));
  const result = await client.instances.query(firstQuery);
  const items = result.items;
  let rowsSoFar = countItems(items);
  let cursors = result.nextCursor;

  while (Object.keys(cursors ?? {}).length && rowsSoFar < totalBudget) {
    const remaining = totalBudget - rowsSoFar;
    const pagedQuery = withPerViewLimit(baseQuery, Math.min(remaining, CDF_MAX_PAGE_SIZE));
    const nextResult = await client.instances.query({ ...pagedQuery, cursors });
    const hasItems = Object.values(nextResult?.items ?? {}).some((item) => item.length);
    if (!hasItems) {
      break;
    }
    appendPageItems(items, nextResult?.items);
    rowsSoFar = countItems(items);
    cursors = nextResult?.nextCursor;
  }
  return items;
}

/**
 * Relaxes query constraints progressively based on retry attempt number.
 *
 * Attempt 1: Remove sort constraints
 * Attempt 2: Increase limit (20 -> 50 -> 100)
 * Attempt 3: Remove non-essential filters (keep hasData filter)
 *
 * @param query - Original query object
 * @param attempt - Current retry attempt (1-based)
 * @returns Relaxed query with fewer constraints
 */
function relaxQueryConstraints(query: QueryRequest, attempt: number) {
  if (!query || typeof query !== 'object') {
    return query;
  }

  return produce(query, (draft) => {
    const withSection = draft.with as Record<string, unknown> | undefined;
    if (!withSection) {
      return;
    }

    for (const viewKey of Object.keys(withSection)) {
      const viewConfig = withSection[viewKey] as Record<string, unknown>;
      if (!viewConfig || typeof viewConfig !== 'object') {
        continue;
      }

      switch (attempt) {
        case 1:
          // Remove sort constraints - can cause issues with missing/null properties
          delete viewConfig.sort;
          break;

        case 2:
          // Increase limit progressively
          if (typeof viewConfig.limit === 'number') {
            viewConfig.limit = Math.min(viewConfig.limit * 2.5, 100);
          } else {
            viewConfig.limit = 50;
          }
          break;

        case 3:
          // Remove non-essential filters, keep only hasData
          if (viewConfig.nodes && typeof viewConfig.nodes === 'object') {
            const nodes = viewConfig.nodes as Record<string, unknown>;
            const filter = nodes.filter as Record<string, unknown> | undefined;

            if (filter?.and && Array.isArray(filter.and)) {
              // Keep only hasData filters, remove everything else
              filter.and = filter.and.filter(
                (f: unknown) => f && typeof f === 'object' && 'hasData' in (f as Record<string, unknown>),
              );
            }
          }
          break;
      }
    }
  });
}

/**
 * Generates alternative query suggestions based on original query and error.
 *
 * Parses error message for hints (missing property, invalid value, etc.)
 * and generates queries that avoid the problematic filter.
 *
 * @param originalQuery - The query that failed
 * @param error - The error that occurred
 * @returns Array of alternative query suggestions
 */
function generateAlternativeQueries(originalQuery: unknown, error: Error): AlternativeQuery[] {
  const alternatives: AlternativeQuery[] = [];
  const errorMessage = error.message.toLowerCase();

  if (!originalQuery || typeof originalQuery !== 'object') {
    return alternatives;
  }

  const query = originalQuery as Record<string, unknown>;
  const withSection = query.with as Record<string, unknown> | undefined;
  if (!withSection) {
    return alternatives;
  }

  // Get view keys for context
  const viewKeys = Object.keys(withSection);
  const primaryView = viewKeys[0];

  // Suggest query without filters if error mentions filter/property issues
  if (
    errorMessage.includes('property') ||
    errorMessage.includes('filter') ||
    errorMessage.includes('field') ||
    errorMessage.includes('not found')
  ) {
    const noFiltersQuery = produce(query, (draft) => {
      const draftWith = draft.with as Record<string, unknown>;
      for (const viewKey of Object.keys(draftWith)) {
        const viewConfig = draftWith[viewKey] as Record<string, unknown>;
        if (viewConfig?.nodes && typeof viewConfig.nodes === 'object') {
          const nodes = viewConfig.nodes as Record<string, unknown>;
          const filter = nodes.filter as Record<string, unknown> | undefined;
          if (filter?.and && Array.isArray(filter.and)) {
            // Keep only hasData
            filter.and = filter.and.filter(
              (f: unknown) => f && typeof f === 'object' && 'hasData' in (f as Record<string, unknown>),
            );
          }
        }
      }
    });

    alternatives.push({
      description: `Query ${primaryView ?? 'view'} without filters`,
      query: noFiltersQuery,
    });
  }

  // Suggest removing sort if error mentions sorting/ordering
  if (errorMessage.includes('sort') || errorMessage.includes('order') || errorMessage.includes('null')) {
    const noSortQuery = produce(query, (draft) => {
      const draftWith = draft.with as Record<string, unknown>;
      for (const viewKey of Object.keys(draftWith)) {
        const viewConfig = draftWith[viewKey] as Record<string, unknown>;
        delete viewConfig.sort;
      }
    });

    alternatives.push({
      description: `Query ${primaryView ?? 'view'} without sorting`,
      query: noSortQuery,
    });
  }

  // Suggest increasing limit if error mentions empty/limit
  if (errorMessage.includes('empty') || errorMessage.includes('limit') || errorMessage.includes('no results')) {
    const higherLimitQuery = produce(query, (draft) => {
      const draftWith = draft.with as Record<string, unknown>;
      for (const viewKey of Object.keys(draftWith)) {
        const viewConfig = draftWith[viewKey] as Record<string, unknown>;
        viewConfig.limit = 100;
      }
    });

    alternatives.push({
      description: `Query ${primaryView ?? 'view'} with higher limit (100)`,
      query: higherLimitQuery,
    });
  }

  // Always suggest a minimal query as last resort
  const minimalQuery = produce(query, (draft) => {
    const draftWith = draft.with as Record<string, unknown>;
    for (const viewKey of Object.keys(draftWith)) {
      const viewConfig = draftWith[viewKey] as Record<string, unknown>;
      delete viewConfig.sort;
      viewConfig.limit = 20;
      if (viewConfig?.nodes && typeof viewConfig.nodes === 'object') {
        const nodes = viewConfig.nodes as Record<string, unknown>;
        const filter = nodes.filter as Record<string, unknown> | undefined;
        if (filter?.and && Array.isArray(filter.and)) {
          filter.and = filter.and.filter(
            (f: unknown) => f && typeof f === 'object' && 'hasData' in (f as Record<string, unknown>),
          );
        }
      }
    }
  });

  alternatives.push({
    description: `Minimal query for ${primaryView ?? 'view'} (no filters, no sort)`,
    query: minimalQuery,
  });

  return alternatives;
}

/**
 * Executes a CDF query with automatic retry and recovery.
 *
 * On failure:
 * 1. Retries with progressively relaxed constraints up to maxRetries
 * 2. If all retries fail, generates alternative query suggestions
 * 3. Returns structured result with all context (never throws)
 *
 * @param query - The CDF instance query to execute
 * @param context - Recovery context with client and options
 * @returns RecoveryResult with success/failure status and context
 */
export async function executeQueryWithRecovery<T>(
  query: QueryRequest,
  context: RecoveryContext,
): Promise<RecoveryResult<T>> {
  const maxRetries = context.maxRetries ?? DEFAULT_MAX_RETRIES;
  let currentQuery = query;
  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // The caller's per-view `limit` is treated as a TOTAL row budget across
      // pagination, not a per-page value. CDF caps every page at
      // CDF_MAX_PAGE_SIZE; `paginatePages` splits the budget accordingly.
      const items = await paginatePages(context.client, currentQuery, getTotalBudget(currentQuery));
      return {
        success: true,
        items: items as T,
        attempts: attempt + 1,
        finalQuery: currentQuery,
      };
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxRetries) {
        // Relax constraints for next attempt
        currentQuery = relaxQueryConstraints(currentQuery, attempt + 1);
        context.onRetry?.(attempt + 1, currentQuery);
      }
    }
  }

  // All retries exhausted - generate alternatives
  const suggestedAlternatives = lastError ? generateAlternativeQueries(query, lastError) : [];

  return {
    success: false,
    attempts: maxRetries + 1,
    finalQuery: currentQuery,
    error: lastError?.message ?? 'Query failed after maximum retries',
    suggestedAlternatives,
  };
}
